import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSamlSetToken } from '../../../helpers/api/auth/auth';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { RiShieldLine } from 'react-icons/ri';

const SSOCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [twoFaRequired, setTwoFaRequired] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tokenValue, setTokenValue] = useState<string | null>(null);
  const { mutateAsync: setToken, isPending } = useSamlSetToken();

  const finalizeAuth = (result: any) => {
    const force2faRequired =
      result?.force_2fa_setup_required ||
      result?.data?.force_2fa_setup_required;
    if (force2faRequired) {
      navigate('/2fa-setup');
      return;
    }
    navigate('/home');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          setError('No token found in URL');
          setLoading(false);
          return;
        }

        setTokenValue(token);
        const result: any = await setToken({ token });
        const payload = result?.data ?? result;
        if (payload?.twofa_required) {
          setTwoFaRequired(true);
          setLoading(false);
          return;
        }
        finalizeAuth(payload);
      } catch (err) {
        if (axios.isAxiosError(err) && err.response) {
          setError(JSON.stringify(err.response.data));
        } else {
          setError('An unexpected error occurred');
        }
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, location, setToken]);

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!tokenValue) {
      setError('Missing SSO token.');
      return;
    }
    try {
      const result: any = await setToken({ token: tokenValue, totp_code: totpCode });
      const payload = result?.data ?? result;
      if (payload?.twofa_required) {
        setError('Invalid authentication code.');
        return;
      }
      finalizeAuth(payload);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(JSON.stringify(err.response.data));
      } else {
        setError('An unexpected error occurred');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">Processing SSO login...</h2>
        </div>
      </div>
    );
  }

  if (error && !twoFaRequired) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-md p-6 max-w-md">
          <h2 className="text-xl font-semibold text-red-700 mb-4">Authentication Error</h2>
          <p className="text-red-600 mb-4">We encountered an error during SSO authentication:</p>
          <div className="bg-white p-3 rounded border border-red-100 overflow-auto">
            <pre className="text-sm text-gray-800">{error}</pre>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 px-4 py-2 bg-primary text-white rounded bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (twoFaRequired) {
    return (
      <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
        <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <RiShieldLine className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
                Two-Factor Authentication
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enter the authentication code to continue.
              </p>
            </div>

            <form onSubmit={handleVerify} className="space-y-4">
              <FormField label="Authentication Code" htmlFor="totp">
                <Input
                  id="totp"
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  maxLength={6}
                  minLength={6}
                  placeholder="Enter 6-digit code"
                  autoFocus
                  className="text-center text-lg tracking-wider bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600"
                />
              </FormField>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400 text-center">
                    {error}
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  disabled={isPending || totpCode.length !== 6}
                >
                  <RiShieldLine className="w-4 h-4 mr-2" />
                  {isPending ? 'Verifying...' : 'Verify Code'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate('/login')}
                  disabled={isPending}
                >
                  Back to Login
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SSOCallback;
